import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';

export default function CouponManagement() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    discountType: 'percentage',
    discountValue: '',
    maxUses: '',
    validFrom: '',
    validUntil: '',
    isActive: true
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await fetch('/api/billing/admin/coupons', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCoupons(data);
      } else {
        setError('Failed to load coupons');
      }
    } catch (error) {
      setError('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCoupon = async () => {
    try {
      const response = await fetch('/api/billing/admin/coupons', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess('Coupon created successfully!');
        setShowCreateDialog(false);
        resetForm();
        fetchCoupons();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to create coupon');
      }
    } catch (error) {
      setError('Failed to create coupon');
    }
  };

  const handleUpdateCoupon = async () => {
    try {
      const response = await fetch(`/api/billing/admin/coupons/${editingCoupon.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess('Coupon updated successfully!');
        setEditingCoupon(null);
        resetForm();
        fetchCoupons();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update coupon');
      }
    } catch (error) {
      setError('Failed to update coupon');
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;

    try {
      const response = await fetch(`/api/billing/admin/coupons/${couponId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setSuccess('Coupon deleted successfully!');
        fetchCoupons();
      } else {
        setError('Failed to delete coupon');
      }
    } catch (error) {
      setError('Failed to delete coupon');
    }
  };

  const handleEditCoupon = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      discountType: coupon.discount_type,
      discountValue: coupon.discount_value,
      maxUses: coupon.max_uses || '',
      validFrom: coupon.valid_from ? coupon.valid_from.split('T')[0] : '',
      validUntil: coupon.valid_until ? coupon.valid_until.split('T')[0] : '',
      isActive: coupon.is_active
    });
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discountType: 'percentage',
      discountValue: '',
      maxUses: '',
      validFrom: '',
      validUntil: '',
      isActive: true
    });
    setError('');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Coupon code copied to clipboard!');
  };

  const getStatusColor = (coupon) => {
    if (!coupon.is_active) return 'error';
    if (coupon.max_uses && coupon.current_uses >= coupon.max_uses) return 'warning';
    if (coupon.valid_until && new Date(coupon.valid_until) < new Date()) return 'error';
    return 'success';
  };

  const getStatusText = (coupon) => {
    if (!coupon.is_active) return 'Inactive';
    if (coupon.max_uses && coupon.current_uses >= coupon.max_uses) return 'Usage Limit Reached';
    if (coupon.valid_until && new Date(coupon.valid_until) < new Date()) return 'Expired';
    return 'Active';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Coupon Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowCreateDialog(true)}
        >
          Create Coupon
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Discount</TableCell>
              <TableCell>Usage</TableCell>
              <TableCell>Valid From</TableCell>
              <TableCell>Valid Until</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {coupons.map((coupon) => (
              <TableRow key={coupon.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {coupon.code}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(coupon.code)}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  {coupon.discount_type === 'percentage' 
                    ? `${coupon.discount_value}%` 
                    : `$${coupon.discount_value}`}
                </TableCell>
                <TableCell>
                  {coupon.current_uses}
                  {coupon.max_uses && ` / ${coupon.max_uses}`}
                </TableCell>
                <TableCell>
                  {new Date(coupon.valid_from).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  {coupon.valid_until 
                    ? new Date(coupon.valid_until).toLocaleDateString()
                    : 'No Expiry'
                  }
                </TableCell>
                <TableCell>
                  <Chip 
                    label={getStatusText(coupon)} 
                    color={getStatusColor(coupon)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleEditCoupon(coupon)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteCoupon(coupon.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog 
        open={showCreateDialog || !!editingCoupon} 
        onClose={() => {
          setShowCreateDialog(false);
          setEditingCoupon(null);
          resetForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Coupon Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              fullWidth
              required
            />
            
            <FormControl fullWidth>
              <InputLabel>Discount Type</InputLabel>
              <Select
                value={formData.discountType}
                onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
                label="Discount Type"
              >
                <MenuItem value="percentage">Percentage</MenuItem>
                <MenuItem value="fixed">Fixed Amount</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label={`Discount Value ${formData.discountType === 'percentage' ? '(%)' : '($)'}`}
              type="number"
              value={formData.discountValue}
              onChange={(e) => setFormData({ ...formData, discountValue: parseFloat(e.target.value) })}
              fullWidth
              required
            />
            
            <TextField
              label="Maximum Uses (optional)"
              type="number"
              value={formData.maxUses}
              onChange={(e) => setFormData({ ...formData, maxUses: e.target.value ? parseInt(e.target.value) : '' })}
              fullWidth
            />
            
            <TextField
              label="Valid From"
              type="date"
              value={formData.validFrom}
              onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />
            
            <TextField
              label="Valid Until (optional)"
              type="date"
              value={formData.validUntil}
              onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowCreateDialog(false);
              setEditingCoupon(null);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={editingCoupon ? handleUpdateCoupon : handleCreateCoupon}
            variant="contained"
          >
            {editingCoupon ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 